import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Link } from 'react-router-dom';
import { BarChart2, Globe, TrendingUp, RefreshCw, Palette } from 'lucide-react';
import { toast } from 'react-toastify';

// ── Site-wide Accent Colour Picker ──────────────────────────
const PRESET_COLORS = [
  { label: 'Gold (Default)', value: '#C5A52A' },
  { label: 'Civic Blue',     value: '#2A6FC5' },
  { label: 'Forest Green',   value: '#2AC57A' },
  { label: 'Burgundy',       value: '#C52A4A' },
  { label: 'Slate',          value: '#6B7280' },
];

function SiteColorPicker() {
  const [color, setColor] = useState('#C5A52A');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/site-settings').then(res => {
      const c = res.data?.accent_color || '#C5A52A';
      setColor(c);
    }).catch(() => {});
  }, []);

  const applyPreview = (c) => {
    setColor(c);
    setDirty(true);
    document.documentElement.style.setProperty('--brand-accent', c);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/site-settings', { accent_color: color });
      document.documentElement.style.setProperty('--brand-accent', color);
      toast.success('Accent colour saved');
      setDirty(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 border border-gray-800 rounded-sm p-5 bg-gray-950">
      <div className="flex items-center gap-2 mb-4">
        <Palette className="w-4 h-4 text-[#C5A52A]" />
        <h3 className="text-sm font-mono uppercase tracking-wider text-gray-400">Brand Accent Colour</h3>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {PRESET_COLORS.map(p => (
          <button key={p.value} title={p.label} onClick={() => applyPreview(p.value)}
            style={{ backgroundColor: p.value, outline: color === p.value ? `2px solid ${p.value}` : 'none', outlineOffset: '2px' }}
            className="w-8 h-8 rounded-full transition-transform hover:scale-110" />
        ))}
        <div className="flex items-center gap-2 ml-2">
          <label className="text-xs text-gray-500 font-mono">Custom:</label>
          <input type="color" value={color} onChange={e => applyPreview(e.target.value)}
            className="w-10 h-8 rounded cursor-pointer bg-transparent border border-gray-700" />
          <span className="text-xs text-gray-500 font-mono">{color}</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving || !dirty}
          className="px-4 py-2 bg-[#C5A52A] hover:bg-yellow-500 text-black font-mono text-xs uppercase rounded disabled:opacity-40 transition-colors">
          {saving ? 'Saving...' : 'Save Colour'}
        </button>
        {dirty && <span className="text-xs text-yellow-500 font-mono">Unsaved — preview active on this page</span>}
      </div>
    </div>
  );
}


const StatCard = ({ label, value, sub, color = 'text-white', bg = 'bg-gray-900' }) => (
  <div className={`${bg} border border-gray-800 p-5 rounded-sm`}>
    <p className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-3xl font-black ${color}`}>{value}</p>
    {sub && <p className="text-gray-600 text-xs mt-1 font-mono">{sub}</p>}
  </div>
);

const OverviewTab = ({ posts = [], users = [], messages = [], ads = [], reports = [], takedowns = [], realtorAds = [], storeItems = [] }) => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [pointsStats, setPointsStats] = useState({ total_issued: 0, estimated_liability_cents: 0 });
  const [petitionStats, setPetitionStats] = useState({ petition_consent: 0, marketing_consent: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');

  const fetchAnalytics = async (period = analyticsPeriod) => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const res = await api.get(`/analytics/overview?period=${period}&date=today`);
      setAnalytics(res.data);
    } catch (err) {
      setAnalyticsError(err?.response?.data?.detail || 'Could not connect to Matomo');
    } finally { setAnalyticsLoading(false); }
  };

  useEffect(() => {
    // Fetch points liability
    api.get('/admin/points-summary').then(r => setPointsStats(r.data)).catch(() => {});
    api.get('/admin/petition-count').then(r => setPetitionStats(r.data)).catch(() => {});
    // Load analytics on mount
    fetchAnalytics();
    // Build recent activity
    const activity = [
      ...posts.slice(0, 5).map(p => ({
        type: 'post',
        text: `New ${p.category || 'post'} in ${p.user_ward || p.ward || 'Unknown Ward'}`,
        sub: p.user_name || 'Anonymous',
        time: p.created_at,
        color: p.category === 'issue' ? 'text-yellow-400' : 'text-green-400',
        icon: p.category === 'issue' ? '⚠' : p.category === 'positive' ? '★' : '💬'
      })),
      ...messages.slice(0, 3).map(m => ({
        type: 'message',
        text: `Message to ${m.councillor_name || 'councillor'}`,
        sub: m.status === 'replied' ? '✓ Replied' : '⏳ Pending',
        time: m.created_at,
        color: m.status === 'replied' ? 'text-green-400' : 'text-yellow-400',
        icon: '✉'
      }))
    ]
      .filter(a => a.time)
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, 8);
    setRecentActivity(activity);
  }, [posts, messages]);

  const activePosts = posts.filter(p => p.status === 'active').length;
  const issuePosts = posts.filter(p => p.category === 'issue').length;
  const pendingMsgs = messages.filter(m => m.status === 'pending').length;
  const verifiedUsers = users.filter(u => u.is_verified).length;
  const pendingReports = reports.filter(r => r.status === 'pending').length;
  const pendingTakedowns = takedowns.filter(t => t.status === 'pending').length;
  const activeAds = ads.filter(a => a.is_active).length;
  const replyRate = messages.length > 0
    ? Math.round((messages.filter(m => m.status === 'replied' || m.status === 'redemption').length / messages.length) * 100)
    : 0;

  const wardActivity = {};
  posts.forEach(p => {
    const w = p.user_ward || p.ward || 'Unknown';
    wardActivity[w] = (wardActivity[w] || 0) + 1;
  });
  const topWards = Object.entries(wardActivity)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  // Revenue calculations from DB records
  const activeRealtorAds = realtorAds.filter(a => a.is_active);
  const pendingSetup = [
    ...ads.filter(a => a.pending_review),
    ...realtorAds.filter(a => a.pending_review),
  ];
  const monthlyRealtorRevenue = activeRealtorAds.reduce((sum, a) => {
    if (a.plan === 'monthly_nc') return sum + 14900;
    if (a.plan === 'yearly')    return sum + Math.round(150000 / 12);
    if (a.plan === 'mls_elite') return sum + Math.round(500000 / 12);
    return sum;
  }, 0);
  const activeStandardAds = ads.filter(a => a.is_active && a.ad_type === 'standard');
  const monthlyRunRate = monthlyRealtorRevenue + (activeStandardAds.length * 4000);
  const fmt = (cents) => `$${(cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-8">

      {/* ── Revenue + Points Liability ── */}
      <div>
        <h2 className="text-xl font-bold text-white mb-4 font-mono uppercase tracking-wider">
          💰 Revenue & Liability
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Monthly Run-Rate"
            value={fmt(monthlyRunRate)}
            sub={`${activeRealtorAds.length} realtor + ${activeStandardAds.length} standard ads active`}
            color="text-green-400"
          />
          <StatCard
            label="Pending Setups"
            value={pendingSetup.length}
            sub="paid but not yet activated"
            color={pendingSetup.length > 0 ? 'text-yellow-400' : 'text-gray-500'}
          />
          <StatCard
            label="Points Issued"
            value={(pointsStats.total_issued || 0).toLocaleString()}
            sub="across all users"
            color="text-purple-400"
          />
          <StatCard
            label="Points Liability"
            value={fmt(pointsStats.estimated_liability_cents || 0)}
            sub="est. redemption value at store prices"
            color={(pointsStats.estimated_liability_cents || 0) > 5000 ? 'text-orange-400' : 'text-gray-400'}
          />
        </div>
      </div>

      {/* Platform Status */}
      <div>
        <h2 className="text-xl font-bold text-white mb-4 font-mono uppercase tracking-wider">
          📊 Platform Overview
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total Users" value={users.length} sub={`${verifiedUsers} verified`} color="text-blue-400" />
          <StatCard label="Active Posts" value={activePosts} sub={`${issuePosts} issues reported`} color="text-green-400" />
          <StatCard label="Councillor Messages" value={messages.length} sub={`${pendingMsgs} awaiting reply`} color="text-yellow-400" />
          <StatCard label="Reply Rate" value={`${replyRate}%`} sub="of messages responded" color={replyRate >= 70 ? 'text-green-400' : replyRate >= 40 ? 'text-yellow-400' : 'text-red-400'} />
        </div>
        {/* Petition counter */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="bg-[#0a0a0a] border border-[#C5A52A]/30 rounded p-4">
            <p className="font-mono text-[10px] text-[#C5A52A] uppercase tracking-widest mb-1">Petition Consent</p>
            <p className="text-3xl font-black text-white font-mono">{(petitionStats.petition_consent || 0).toLocaleString()}</p>
            <p className="font-mono text-[10px] text-gray-500 mt-1">
              users consenting to petition emails
              {' · '}<span className={petitionStats.petition_consent >= 30000 ? 'text-green-400 font-bold' : 'text-gray-600'}>
                {petitionStats.petition_consent >= 30000 ? '✓ THRESHOLD MET' : `need ${(30000 - (petitionStats.petition_consent || 0)).toLocaleString()} more`}
              </span>
            </p>
            <div className="mt-2 h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#C5A52A] rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, ((petitionStats.petition_consent || 0) / 30000) * 100)}%` }}
              />
            </div>
            <p className="font-mono text-[9px] text-gray-700 mt-1 text-right">
              {Math.round(((petitionStats.petition_consent || 0) / 30000) * 100)}% of 30,000 target
            </p>
          </div>
          <StatCard
            label="Newsletter Opt-In"
            value={(petitionStats.marketing_consent || 0).toLocaleString()}
            sub="users consenting to marketing emails"
            color="text-purple-400"
          />
        </div>
      </div>

      {/* Alerts Row */}
      {(pendingReports > 0 || pendingTakedowns > 0 || pendingMsgs > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {pendingReports > 0 && (
            <div className="bg-red-900/20 border border-red-800 p-4 rounded-sm flex items-center justify-between">
              <div>
                <p className="text-red-400 font-bold text-sm">🚨 {pendingReports} Pending Reports</p>
                <p className="text-gray-500 text-xs mt-0.5">User-flagged posts awaiting review</p>
              </div>
              <span className="text-red-400 font-mono text-2xl font-black">{pendingReports}</span>
            </div>
          )}
          {pendingTakedowns > 0 && (
            <div className="bg-orange-900/20 border border-orange-800 p-4 rounded-sm flex items-center justify-between">
              <div>
                <p className="text-orange-400 font-bold text-sm">⚖ {pendingTakedowns} Takedown Requests</p>
                <p className="text-gray-500 text-xs mt-0.5">Legal removal requests pending</p>
              </div>
              <span className="text-orange-400 font-mono text-2xl font-black">{pendingTakedowns}</span>
            </div>
          )}
          {pendingMsgs > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-800 p-4 rounded-sm flex items-center justify-between">
              <div>
                <p className="text-yellow-400 font-bold text-sm">✉ {pendingMsgs} Unanswered Messages</p>
                <p className="text-gray-500 text-xs mt-0.5">Constituent messages without reply</p>
              </div>
              <span className="text-yellow-400 font-mono text-2xl font-black">{pendingMsgs}</span>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Ward Activity */}
        <div className="bg-gray-900 border border-gray-800 p-5 rounded-sm">
          <h3 className="text-sm font-mono uppercase tracking-wider text-gray-400 mb-4">Most Active Wards</h3>
          {topWards.length > 0 ? (
            <div className="space-y-2">
              {topWards.map(([ward, count], idx) => {
                const pct = Math.round((count / Math.max(...topWards.map(([,c]) => c))) * 100);
                return (
                  <div key={ward} className="flex items-center gap-3">
                    <span className="text-gray-600 font-mono text-xs w-4">{idx + 1}</span>
                    <span className="text-white text-sm flex-1 truncate">{ward}</span>
                    <div className="w-24 bg-gray-800 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-green-500 h-full rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-gray-400 font-mono text-xs w-6 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-600 text-sm">No posts yet</p>
          )}
        </div>

        {/* User Breakdown */}
        <div className="bg-gray-900 border border-gray-800 p-5 rounded-sm">
          <h3 className="text-sm font-mono uppercase tracking-wider text-gray-400 mb-4">User Breakdown</h3>
          <div className="space-y-3">
            {[
              { label: 'Regular Users', count: users.filter(u => u.role === 'user').length, color: 'bg-blue-500' },
              { label: 'Community Leaders', count: users.filter(u => u.role === 'community_leader').length, color: 'bg-green-500' },
              { label: 'Admins', count: users.filter(u => u.role === 'admin').length, color: 'bg-purple-500' },
              { label: 'Banned', count: users.filter(u => u.is_banned).length, color: 'bg-red-500' },
            ].map(({ label, count, color }) => (
              <div key={label} className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                <span className="text-gray-300 text-sm flex-1">{label}</span>
                <span className="text-white font-mono font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-gray-900 border border-gray-800 p-5 rounded-sm md:col-span-2">
          <h3 className="text-sm font-mono uppercase tracking-wider text-gray-400 mb-4">Recent Activity</h3>
          {recentActivity.length > 0 ? (
            <div className="space-y-2">
              {recentActivity.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 py-2 border-b border-gray-800/50 last:border-0">
                  <span className="text-lg">{item.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{item.text}</p>
                    <p className={`text-xs font-mono ${item.color}`}>{item.sub}</p>
                  </div>
                  <span className="text-gray-600 text-xs font-mono whitespace-nowrap">
                    {item.time ? new Date(item.time).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-sm">No recent activity</p>
          )}
        </div>
      </div>

      {/* ── Matomo Analytics Widget ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white font-mono uppercase tracking-wider flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-[#C5A52A]" /> Site Analytics
          </h2>
          <div className="flex items-center gap-3">
            <select
              value={analyticsPeriod}
              onChange={e => { setAnalyticsPeriod(e.target.value); fetchAnalytics(e.target.value); }}
              className="bg-[#111] border border-[#2a2a2a] text-white text-xs font-mono px-3 py-1.5 focus:outline-none focus:border-[#C5A52A]"
              data-testid="analytics-period-select"
            >
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
            <button
              onClick={() => fetchAnalytics()}
              disabled={analyticsLoading}
              className="p-1.5 border border-[#2a2a2a] hover:border-[#C5A52A] text-gray-500 hover:text-white transition-colors disabled:opacity-40"
              data-testid="analytics-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${analyticsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {analyticsLoading && !analytics && (
          <div className="bg-[#0d0d0d] border border-[#1a1a1a] p-8 text-center">
            <RefreshCw className="w-6 h-6 text-[#C5A52A] animate-spin mx-auto mb-2" />
            <p className="text-gray-600 text-xs font-mono">Loading analytics...</p>
          </div>
        )}

        {analyticsError && (
          <div className="bg-[#0d0d0d] border border-red-900/40 p-5">
            <p className="text-red-400 text-xs font-mono mb-1">Analytics unavailable</p>
            <p className="text-gray-600 text-xs font-mono">{analyticsError}</p>
            {analyticsError.includes('BotTracking') && (
              <p className="text-yellow-600 text-xs font-mono mt-2">
                Fix: In Matomo → System → Plugins → deactivate <strong>BotTracking</strong>, then reactivate it to rebuild the table.
              </p>
            )}
          </div>
        )}

        {analytics && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Visits', value: analytics.visits?.nb_visits ?? '—', color: 'text-blue-400' },
                { label: 'Unique Visitors', value: analytics.visits?.nb_uniq_visitors ?? '—', color: 'text-green-400' },
                { label: 'Page Views', value: analytics.visits?.nb_actions ?? '—', color: 'text-[#C5A52A]' },
                { label: 'Avg. Duration', value: analytics.visits?.avg_time_on_site ? `${Math.floor(analytics.visits.avg_time_on_site / 60)}m ${analytics.visits.avg_time_on_site % 60}s` : '—', color: 'text-purple-400' },
              ].map(s => (
                <div key={s.label} className="bg-[#0d0d0d] border border-[#1a1a1a] p-4">
                  <p className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-1">{s.label}</p>
                  <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>

            {analytics.top_pages?.length > 0 && (
              <div className="bg-[#0d0d0d] border border-[#1a1a1a] p-5">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-3">Top Pages</h3>
                <div className="space-y-2">
                  {analytics.top_pages.slice(0, 8).map((p, i) => {
                    const maxV = analytics.top_pages[0]?.nb_visits || 1;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-gray-700 text-xs font-mono w-4 flex-shrink-0">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-white text-xs font-mono truncate">{p.label || '/'}</span>
                            <span className="text-gray-500 text-xs font-mono ml-2 flex-shrink-0">{p.nb_visits} visits</span>
                          </div>
                          <div className="h-1 bg-[#1a1a1a] rounded-full overflow-hidden">
                            <div className="h-full bg-[#C5A52A] rounded-full" style={{ width: `${Math.round((p.nb_visits / maxV) * 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {analytics.top_events?.length > 0 && (
              <div className="bg-[#0d0d0d] border border-[#1a1a1a] p-5">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-3">Top Event Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {analytics.top_events.slice(0, 6).map((e, i) => (
                    <div key={i} className="bg-[#1a1a1a] border border-[#2a2a2a] px-3 py-1.5">
                      <span className="text-white text-xs font-mono">{e.label}</span>
                      <span className="text-[#C5A52A] text-xs font-mono ml-2">{e.nb_events || e.nb_visits}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-gray-700 text-xs font-mono text-right">
              Powered by Matomo · <a href="https://accountability.click" target="_blank" rel="noopener noreferrer" className="hover:text-gray-500 transition-colors">accountability.click</a>
            </p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-900 border border-gray-800 p-5 rounded-sm">
        <h3 className="text-sm font-mono uppercase tracking-wider text-gray-400 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'View All Posts', tab: 'posts', color: 'bg-gray-800 hover:bg-gray-700 text-white' },
            { label: 'Manage Users', tab: 'users', color: 'bg-gray-800 hover:bg-gray-700 text-white' },
            { label: `Reports ${pendingReports > 0 ? `(${pendingReports})` : ''}`, tab: 'reports', color: pendingReports > 0 ? 'bg-red-900 hover:bg-red-800 text-red-300' : 'bg-gray-800 hover:bg-gray-700 text-white' },
            { label: 'Ads & Realtors', tab: 'ads', color: 'bg-gray-800 hover:bg-gray-700 text-white' },
            { label: 'Wards & Councillors', tab: 'wards & councillors', color: 'bg-gray-800 hover:bg-gray-700 text-white' },
          ].map(({ label, tab, color }) => (
            <button
              key={tab}
              onClick={() => window.__adminSetTab && window.__adminSetTab(tab)}
              className={`px-4 py-2 rounded-sm font-mono text-xs uppercase tracking-wider transition-colors ${color}`}
            >
              {label}
            </button>
          ))}
          <Link
            to="/"
            className="px-4 py-2 rounded-sm font-mono text-xs uppercase tracking-wider transition-colors bg-green-900 hover:bg-green-800 text-green-300"
          >
            View Live Site →
          </Link>
        </div>
      </div>

      {/* Site Settings: Accent Colour Picker */}
      <SiteColorPicker />
    </div>
  );
};

export default OverviewTab;
